import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface UserEngagement {
  id: string;
  user_id: string;
  session_count: number;
  actions_count: number;
  first_seen_at: string;
  last_prompted_at: string | null;
  feedback_dismissed_count: number;
  has_given_feedback: boolean;
}

// Configuration for when to show feedback prompts
export const PROMPT_CONFIG = {
  minSessions: 5,           // Show after 5 sessions
  minActions: 10,           // And after 10 actions (check-ins, reviews, etc.)
  minDaysSinceFirstSeen: 7, // And after 7 days of using the app
  cooldownDays: 14,         // Don't show again for 14 days after dismissal
  maxDismissals: 3,         // Stop showing after 3 dismissals
  minCheckIns: 1,           // Require at least 1 check-in before prompting
};

/**
 * Pure eligibility predicate for the feedback prompt. Exported so other prompts
 * (e.g. the App Store review prompt) can check "is feedback about to fire?"
 * against the same user_engagement row without duplicating the rules.
 */
export function shouldShowFeedbackPrompt(
  engagement: UserEngagement | null | undefined,
  checkInCount: number | null | undefined,
): boolean {
  if (!engagement) return false;
  if (engagement.has_given_feedback) return false;
  if (engagement.feedback_dismissed_count >= PROMPT_CONFIG.maxDismissals) return false;

  if (engagement.last_prompted_at) {
    const daysSincePrompt =
      (Date.now() - new Date(engagement.last_prompted_at).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSincePrompt < PROMPT_CONFIG.cooldownDays) return false;
  }

  const daysSinceFirstSeen =
    (Date.now() - new Date(engagement.first_seen_at).getTime()) / (1000 * 60 * 60 * 24);

  return (
    engagement.session_count >= PROMPT_CONFIG.minSessions &&
    engagement.actions_count >= PROMPT_CONFIG.minActions &&
    daysSinceFirstSeen >= PROMPT_CONFIG.minDaysSinceFirstSeen &&
    (checkInCount ?? 0) >= PROMPT_CONFIG.minCheckIns
  );
}

export function useFeedbackPrompt() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [shouldShow, setShouldShow] = useState(false);

  // Fetch user engagement data
  const { data: engagement } = useQuery({
    queryKey: ['user-engagement', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_engagement')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as UserEngagement | null;
    },
    enabled: !!user?.id,
  });

  // Fetch user's check-in count
  const { data: checkInCount } = useQuery({
    queryKey: ['user-checkin-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count, error } = await (supabase as any)
        .from('check_ins_visits')
        .select('*', { head: true, count: 'exact' })
        .eq('user_id', user.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user?.id,
  });

  // Initialize or update session count
  const initSessionMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) return null;

      const { data: existing } = await supabase
        .from('user_engagement')
        .select('id, session_count')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        // Update session count
        const { data, error } = await supabase
          .from('user_engagement')
          .update({ session_count: existing.session_count + 1 })
          .eq('user_id', user.id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      } else {
        // Create new engagement record
        const { data, error } = await supabase
          .from('user_engagement')
          .insert({ user_id: user.id })
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-engagement', user?.id] });
    },
  });

  // Track when user takes an action
  const trackAction = useCallback(async () => {
    if (!user?.id || !engagement) return;

    await supabase
      .from('user_engagement')
      .update({ actions_count: engagement.actions_count + 1 })
      .eq('user_id', user.id);
    
    queryClient.invalidateQueries({ queryKey: ['user-engagement', user?.id] });
  }, [user?.id, engagement, queryClient]);

  // Dismiss the prompt
  const dismissPrompt = useCallback(async () => {
    if (!user?.id || !engagement) return;

    await supabase
      .from('user_engagement')
      .update({ 
        last_prompted_at: new Date().toISOString(),
        feedback_dismissed_count: engagement.feedback_dismissed_count + 1,
      })
      .eq('user_id', user.id);
    
    setShouldShow(false);
    queryClient.invalidateQueries({ queryKey: ['user-engagement', user?.id] });
  }, [user?.id, engagement, queryClient]);

  // Mark that user gave feedback
  const markFeedbackGiven = useCallback(async () => {
    if (!user?.id) return;

    await supabase
      .from('user_engagement')
      .update({ 
        has_given_feedback: true,
        last_prompted_at: new Date().toISOString(),
      })
      .eq('user_id', user.id);
    
    setShouldShow(false);
    queryClient.invalidateQueries({ queryKey: ['user-engagement', user?.id] });
  }, [user?.id, queryClient]);

  // Initialize session on mount
  useEffect(() => {
    if (user?.id) {
      // Check if we already counted this session (using sessionStorage)
      const sessionKey = `feedback_session_${user.id}`;
      if (!sessionStorage.getItem(sessionKey)) {
        initSessionMutation.mutate();
        sessionStorage.setItem(sessionKey, 'true');
      }
    }
  }, [user?.id]);

  // Determine if we should show the prompt (delegates to exported pure predicate)
  useEffect(() => {
    if (!user?.id) {
      setShouldShow(false);
      return;
    }
    setShouldShow(shouldShowFeedbackPrompt(engagement, checkInCount));
  }, [engagement, checkInCount, user?.id]);

  return {
    shouldShowPrompt: shouldShow,
    trackAction,
    dismissPrompt,
    markFeedbackGiven,
    engagement,
  };
}
